import { useCallback, useMemo, useState } from "react";
import { Button, Card, Flex, Text, TextField } from "@radix-ui/themes";
import { ResponseFieldTree } from "./ResponseFieldTree";
import { useSkillApi } from "@/v2/entities/skill/Skill.api";
import { JSONSchema7 } from "json-schema";
import { toast } from "sonner";
import { ParameterEntry, ResponseFieldSelectorProps } from "./ResponseField.model";
import styles from "./ResponseFieldSelector.module.scss";

const PARAM_GRID = "1fr 100px 1fr";

function ParameterRow({
  param,
  value,
  onChange,
}: {
  param: ParameterEntry;
  value: string;
  onChange: (key: string, value: string) => void;
}) {
  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) =>
      onChange(param.key, e.target.value),
    [param.key, onChange]
  );

  return (
    <div
      className={styles.paramGrid}
      style={{ gridTemplateColumns: PARAM_GRID }}
    >
      <div className={styles.paramCell}>
        <Text size="2">
          {param.key}
          {param.required && " *"}
        </Text>
      </div>
      <div className={styles.paramCell}>
        <Text size="2">{param.type}</Text>
      </div>
      <TextField.Root
        size="2"
        placeholder={param.description ? `e.g. ${param.description}` : ""}
        value={value}
        onChange={handleChange}
      />
    </div>
  );
}

export function ResponseFieldSelector({
  skillId,
  parameters,
  sampleResponse,
  responseFields,
  onResponseFieldsChange,
}: ResponseFieldSelectorProps) {
  const { sampleSkill, updateSkill } = useSkillApi();
  const [freshResponse, setFreshResponse] = useState<unknown>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [paramValues, setParamValues] = useState<Record<string, string>>({});

  const displayData = freshResponse ?? sampleResponse;

  const parameterEntries = useMemo<ParameterEntry[]>(() => {
    if (!parameters) return [];
    if ((parameters as { type?: string }).type === "browser") return [];
    const schema = parameters as JSONSchema7;
    if (schema.type !== "object" || !schema.properties) return [];
    const required = new Set(
      Array.isArray(schema.required) ? schema.required : []
    );
    return Object.entries(schema.properties)
      .filter(
        (entry): entry is [string, JSONSchema7] =>
          typeof entry[1] === "object" && entry[1] !== null
      )
      .map(([key, prop]) => ({
        key,
        type: (prop.type as string) ?? "string",
        description: prop.description ?? "",
        required: required.has(key),
      }));
  }, [parameters]);

  const hasParameters = parameterEntries.length > 0;

  const handleParamChange = useCallback((key: string, value: string) => {
    setParamValues((prev) => ({ ...prev, [key]: value }));
  }, []);

  const handleSample = useCallback(async () => {
    if (!skillId) return;
    setLoading(true);
    setError(null);
    try {
      const params = Object.keys(paramValues).length
        ? paramValues
        : undefined;
      const result = await sampleSkill(skillId, params);
      if (result != null) {
        setFreshResponse(result);
      } else {
        setError("Skill test failed");
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "An error occurred");
    } finally {
      setLoading(false);
    }
  }, [skillId, sampleSkill, paramValues]);

  const handleSave = useCallback(async () => {
    if (!skillId) return;
    setSaving(true);
    try {
      await updateSkill({
        id: skillId,
        responseFields: responseFields.length ? responseFields : null,
      });
      toast.success("Response fields saved");
    } catch {
      toast.error("Failed to save response fields");
    } finally {
      setSaving(false);
    }
  }, [skillId, responseFields, updateSkill]);

  const handleClearFilter = useCallback(() => {
    onResponseFieldsChange([]);
  }, [onResponseFieldsChange]);

  if (!skillId) {
    return (
      <Flex align="center" justify="center" className={styles.emptyState}>
        <Text size="2" color="gray">
          Save skill first, then test
        </Text>
      </Flex>
    );
  }

  return (
    <div className={styles.fullHeight}>
      <Flex direction="column" gap="4">
        <div className={styles.section}>
          <Flex justify="between" align="start" gap="4">
            <Flex direction="column" gap="3" className={styles.paramsColumn}>
              <Text size="3" weight="medium">
                Test Request
              </Text>
              {hasParameters ? (
                <>
                  <Text size="2" weight="medium">
                    Test Parameters
                  </Text>
                  <Flex direction="column" gap="2">
                    {parameterEntries.map((param) => (
                      <ParameterRow
                        key={param.key}
                        param={param}
                        value={paramValues[param.key] ?? ""}
                        onChange={handleParamChange}
                      />
                    ))}
                  </Flex>
                </>
              ) : (
                <Text size="2" color="gray">
                  There are no parameters in this tool.
                </Text>
              )}
              {error && (
                <Text size="2" color="red">
                  {error}
                </Text>
              )}
            </Flex>

            <Flex direction="column" align="end" gap="3">
              <Button
                size="2"
                variant="solid"
                color="gray"
                highContrast
                onClick={handleSample}
                loading={loading}
              >
                Map Fields
              </Button>
              {hasParameters && (
                <Card className={styles.helpCard}>
                  <Text size="1" color="gray">
                    These parameters will be sent with the request when
                    testing.
                  </Text>
                </Card>
              )}
            </Flex>
          </Flex>
        </div>

        {displayData != null && (
          <div className={styles.section}>
            <Flex direction="column" gap="3">
              <Flex justify="between" align="center">
                <Text size="3" weight="medium">
                  Response Fields
                </Text>
                <Flex align="center" gap="2">
                  {responseFields.length > 0 && (
                    <Button
                      size="2"
                      variant="soft"
                      color="red"
                      onClick={handleClearFilter}
                    >
                      Clear filter
                    </Button>
                  )}
                  <Button
                    size="2"
                    variant="solid"
                    color="gray"
                    highContrast
                    onClick={handleSave}
                    loading={saving}
                  >
                    Save Fields
                  </Button>
                </Flex>
              </Flex>

              <ResponseFieldTree
                data={displayData}
                selectedFields={responseFields}
                onFieldsChange={onResponseFieldsChange}
              />
            </Flex>
          </div>
        )}
      </Flex>
    </div>
  );
}
